"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const edit_1 = __importDefault(require("@sheet/edit"));
const common_1 = require("@finley-technologies/common");
const helpers_1 = require("./helpers");
const typeguards_1 = require("./typeguards");
const isValidRange = (range) => {
    const { s: start, e: end } = range;
    return start.c >= 0 && start.r >= 0 && end.c >= start.c && end.r >= start.r;
};
const isWithinRange = ({ cellRangeA1, range, }) => {
    const decodedCellRange = edit_1.default.utils.decode_range(cellRangeA1);
    return (decodedCellRange.s.c >= range.s.c &&
        decodedCellRange.s.r >= range.s.r &&
        decodedCellRange.e.c <= range.e.c &&
        decodedCellRange.e.r <= range.e.r);
};
const getCellValue = ({ sheet, cellAddressA1, importFormat, }) => {
    const cellValue = sheet[cellAddressA1];
    if (!cellValue) {
        return {
            preSanitized: false,
            value: '', // return empty value for invalid cell.
        };
    }
    let value;
    switch (importFormat) {
        case 'raw':
            value = cellValue.v;
            break;
        case 'formula':
            // read the formula value if it is available, if not use raw value.
            // this is what our xlsx2csv2 config expects.
            value = cellValue.f || cellValue.v;
            break;
        case 'formatted':
            value = cellValue.w;
            break;
        default:
            (0, common_1.finleyAssertUnreachable)(importFormat);
    }
    const valueIsDate = value instanceof Date;
    // convert any dates to UTC string
    // @xlsx2Csv: we do not have a standard format for dateTime values.
    // we need to update this to look at field type to be DateTime, and write to
    // standard format.
    // XLSX apis read date values (with missing times) as JS date objects
    // in UTC timezone. with time set to 07:00:00.
    // converting into UTC string will give us the date in yyyy-mm-dd format.
    // matching the date as seen in the excel sheet.
    const updatedValue = valueIsDate
        ? common_1.date.dateToUTCFullDateString(value)
        : value;
    const preSanitized = valueIsDate;
    return {
        preSanitized,
        value: updatedValue,
    };
};
const getMatchingSheet = ({ workbook, sheetName, isSheetNameRegex, }) => {
    const sheetNames = workbook.SheetNames;
    if (isSheetNameRegex) {
        // sheetName is a regex, we need to find the sheet that matches the regex
        const matchingSheetNames = sheetNames.filter((name) => new RegExp(sheetName).test(name));
        // ensure that just one name matches the regex
        (0, common_1.finleyAssert)(matchingSheetNames.length === 1, `Expected 1 matching sheet[${sheetName}], found ${matchingSheetNames.length} sheets.`);
        const sheet = workbook.Sheets[matchingSheetNames[0]];
        return sheet;
    }
    (0, common_1.finleyAssert)(sheetNames.includes(sheetName), `Sheet with name ${sheetName} not found in workbook`);
    return workbook.Sheets[sheetName];
};
const validateRegEx = (regex) => {
    try {
        // eslint-disable-next-line no-new
        new RegExp(regex);
    }
    catch (err) {
        throw new Error(`Invalid regex: ${regex}`);
    }
};
const validateSheetName = ({ sheetName, isSheetNameRegex, }) => {
    if (isSheetNameRegex) {
        validateRegEx(sheetName);
    }
};
const validateExtractValuesConfig = (transformation) => {
    const { fields } = transformation;
    (0, common_1.finleyAssert)(fields.length > 0, 'fields must have at least one entry');
    const uniqueFieldNames = new Set();
    fields.forEach((field) => {
        const { cell, field: fieldName, sheetName, isSheetNameRegex } = field;
        validateSheetName({ sheetName, isSheetNameRegex });
        // we expect cell of the format M16 representing a single cell.
        const decodedCellRange = edit_1.default.utils.decode_range(cell);
        const { s: start, e: end } = decodedCellRange;
        (0, common_1.finleyAssert)(start.r === end.r && start.c === end.c, `[cell:${cell}] is not valid. Expected to represent single cell`);
        (0, common_1.finleyAssert)(start.c >= 0 && start.r >= 0, `[field:${fieldName}] Invalid [columnIndex:${start.c}][rowIndex:${start.r}] for [cellAddress:${cell}]`);
        (0, common_1.finleyAssert)(!uniqueFieldNames.has(fieldName), `Duplicate field name: ${fieldName}`);
        uniqueFieldNames.add(fieldName);
        // @xlsx2Csv:TODO: validate field (date must be in yyyy-mm-dd format)
        // this is because we get date/times as js objects.
    });
};
const getOutputDataTapeValues = (transformation) => {
    validateExtractValuesConfig(transformation);
    // output tape definition is decided by sanitize.
    // lets call sanitize helper to get the output tape definition.
    const sanitizeTransformation = {
        operation: 'sanitize',
        srcDataTape: {
            baseFields: transformation.fields.map((fieldDef) => {
                const { sheetName: _ununsed1, isSheetNameRegex: _ununsed2, cell: _ununsed3, importFormat: _ununsed4 } = fieldDef, rest = __rest(fieldDef, ["sheetName", "isSheetNameRegex", "cell", "importFormat"]);
                return rest;
            }),
            delimiter: ',',
        },
        // Note:we do not want to sanitize field names
        // to give flexibility to user to decorate them with special characters.
        // if they wanted sanitized names, they can always specifify fields names as such.
        sanitizeFieldNames: false,
    };
    return (0, helpers_1.getSanitizeTransformOpOutputDataTape)(sanitizeTransformation);
};
const doTransformValues = ({ xlsBuffer, transformation, }) => {
    const { fields } = transformation;
    const workbook = edit_1.default.read(xlsBuffer, {
        type: 'buffer',
        // cellFormula: true, will get us formula along with raw and formated values
        // we always extract cellFormula, as our config may want it.
        cellFormula: true,
        // we want date values to be read as JS Dates
        // Note this only affect raw values. formatted values are always strings.
        cellDates: true,
    });
    const data = {};
    const errors = [];
    fields.forEach((entry) => {
        const { sheetName, cell: cellAddressA1, field, importFormat, isSheetNameRegex, } = entry;
        const pushNewError = (message) => errors.push({
            type: 'xlsx2Csv',
            sheetName,
            cell: cellAddressA1,
            fieldName: field,
            message,
        });
        try {
            const sheet = getMatchingSheet({
                workbook,
                sheetName,
                isSheetNameRegex: !!isSheetNameRegex,
            });
            const sheetRangeA1 = sheet['!ref'];
            if (!sheetRangeA1) {
                pushNewError('Sheet has no range');
                return;
            }
            const sheetRange = edit_1.default.utils.decode_range(sheetRangeA1);
            if (!isWithinRange({ cellRangeA1: cellAddressA1, range: sheetRange })) {
                pushNewError(`[cell:${cellAddressA1}] is out of sheet range.`);
                return;
            }
            const cellValue = getCellValue({
                sheet,
                cellAddressA1,
                importFormat,
            });
            const { preSanitized, value } = cellValue;
            // sanitize the field
            const sanitizedValue = (0, helpers_1.getSanitizedDataTapeField)(entry, value, preSanitized);
            data[field] = sanitizedValue;
        }
        catch (err) {
            if ((0, typeguards_1.isInvalidField)(err)) {
                // sanitize throws this kind of error convert it to Xlsx2CsvError
                const message = `${err.name}: with [value:${err.value}]. Expected type: ${err.expectedType}`;
                pushNewError(message);
                return; // continue in forEach loop
            }
            // for all other errors push them as is.
            const message = common_1.error.getErrorMessage(err);
            pushNewError(message);
        }
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    return {
        isValid: true,
        data: {
            outputBuffer: common_1.csvConversion.csvArrayToFinleyBuffer({
                csvArray: [data],
                newline: '\n', // arrayToCsvBuffer uses \r\n by default
            }),
        },
        supplementals: [],
    };
};
const validateRangeConfig = (transformation) => {
    const { fields, sheetName, isSheetNameRegex } = transformation;
    validateSheetName({
        sheetName,
        isSheetNameRegex,
    });
    (0, common_1.finleyAssert)(fields.length > 0, 'fields must have at least one entry');
    // validate that headerRange if specified is a single row
    // A1 notation.
    const { headerRange, dataEndRange } = transformation;
    // we expect headerRange of the format A1:Z1
    // unfortunately XLSX.utils.decode_range does not throw an error
    // for single cell input like A1Z1
    (0, common_1.finleyAssert)(headerRange.includes(':'), `headerRange:${headerRange} is not valid. It must be of the format A1:Z1`);
    const decodedHeaderRange = edit_1.default.utils.decode_range(headerRange);
    (0, common_1.finleyAssert)(isValidRange(decodedHeaderRange), `[headerRange:${headerRange}] is not valid.`);
    const { s: headerStart, e: headerEnd } = decodedHeaderRange;
    if (dataEndRange) {
        // we expect dataEndRange of the format A5:Z5
        // unfortunately XLSX.utils.decode_range does not throw an error
        // for single cell input like A1Z1
        (0, common_1.finleyAssert)(dataEndRange.includes(':'), `dataEndRange:${dataEndRange} is not valid. It must be of the format A5:Z5`);
        const decodedDataEndRange = edit_1.default.utils.decode_range(dataEndRange);
        (0, common_1.finleyAssert)(isValidRange(decodedDataEndRange), `[dataEndRange:${dataEndRange}] is not valid.`);
        const { s: dataEndRangeStart, e: dataEndRangeEnd } = decodedDataEndRange;
        (0, common_1.finleyAssert)(dataEndRangeStart.r === dataEndRangeEnd.r, `[dataEndRange:${dataEndRange}] must be a single row range`);
        (0, common_1.finleyAssert)(dataEndRangeStart.c === headerStart.c &&
            dataEndRangeEnd.c === headerEnd.c, `[dataEndRange:${dataEndRange}] must have same columns as [headerRange:${headerRange}]`);
        (0, common_1.finleyAssert)(dataEndRangeStart.r > headerEnd.r, `[dataEndRange:${dataEndRange}] must be after [headerRange:${headerRange}]`);
    }
    const uniqueFieldNames = new Set();
    fields.forEach((field) => {
        const { field: fieldName } = field;
        const sanitizedFieldName = (0, helpers_1.getSanitizedFieldName)(fieldName, true);
        (0, common_1.finleyAssert)(!uniqueFieldNames.has(sanitizedFieldName), `Duplicate field name: ${fieldName} (sanitized: ${sanitizedFieldName})`);
        uniqueFieldNames.add(sanitizedFieldName);
    });
};
const getOutputDataTapeRange = (transformation) => {
    validateRangeConfig(transformation);
    // output tape definition is decided by sanitize.
    // lets call sanitize helper to get the output tape definition.
    const sanitizeTransformation = {
        operation: 'sanitize',
        srcDataTape: {
            baseFields: transformation.fields.map((fieldDef) => {
                const { importFormat: _ununsed4 } = fieldDef, rest = __rest(fieldDef, ["importFormat"]);
                return rest;
            }),
            delimiter: ',',
        },
        // Note: for range mode we do want to sanitize field names
        // since field names specified are not controlled by us, but they
        // are what comes from the excel sheet.
        sanitizeFieldNames: true,
    };
    return (0, helpers_1.getSanitizeTransformOpOutputDataTape)(sanitizeTransformation);
};
const getDataRowRange = ({ sheetRange, decodedHeaderRange, dataEndRange, }) => {
    // data starts from the row after header.
    const startRowIndex = decodedHeaderRange.e.r + 1;
    const decodedEndRange = dataEndRange
        ? edit_1.default.utils.decode_range(dataEndRange)
        : null;
    // data ends at dataEndRange if specified otherwise end of the sheetRange.
    const endRowIndex = decodedEndRange ? decodedEndRange.e.r : sheetRange.e.r;
    return { startRowIndex, endRowIndex };
};
const doTransformRange = ({ xlsBuffer, transformation, }) => {
    const { fields, sheetName, isSheetNameRegex, headerRange, dataEndRange, } = transformation;
    const workbook = edit_1.default.read(xlsBuffer, {
        type: 'buffer',
        cellFormula: true,
        // we want date values to be read as JS Dates
        cellDates: true,
    });
    const sheet = getMatchingSheet({
        workbook,
        sheetName,
        isSheetNameRegex: !!isSheetNameRegex,
    });
    const sheetRangeA1 = sheet['!ref'];
    (0, common_1.finleyAssert)(sheetRangeA1, `[Sheet:${sheetName} has no range`);
    const sheetRange = edit_1.default.utils.decode_range(sheetRangeA1);
    const decodedHeaderRange = edit_1.default.utils.decode_range(headerRange);
    (0, common_1.finleyAssert)(isValidRange(decodedHeaderRange), `[headerRange:${headerRange}] is not valid.`);
    (0, common_1.finleyAssert)(isWithinRange({
        cellRangeA1: headerRange,
        range: sheetRange,
    }), `[headerRange:${headerRange}] is out of sheet range.`);
    const effectiveRowRange = getDataRowRange({
        decodedHeaderRange,
        dataEndRange,
        sheetRange,
    });
    // walk the headers and create a map of value => column index.
    // we will use this map to associate the field definitions with the columns.
    // Note we are keeping column values as array, as header value can be repeated.
    // if a field refers to a header that is found in multiple columns
    // we will report this as a runtime error.
    const headerValueToColumns = new Map();
    // eslint-disable-next-line no-restricted-syntax
    for (let columnIndex = decodedHeaderRange.s.c; columnIndex <= decodedHeaderRange.e.c; 
    // eslint-disable-next-line no-plusplus
    columnIndex++) {
        // eslint-disable-next-line no-restricted-syntax
        for (let rowIndex = decodedHeaderRange.s.r; rowIndex <= decodedHeaderRange.e.r; 
        // eslint-disable-next-line no-plusplus
        rowIndex++) {
            const cellAddressA1 = edit_1.default.utils.encode_cell({
                c: columnIndex,
                r: rowIndex,
            });
            const cellValue = getCellValue({
                sheet,
                cellAddressA1,
                importFormat: 'raw',
            });
            const { value: rawColumnValue } = cellValue;
            const sanitizedColumnValue = (0, helpers_1.getSanitizedFieldName)(rawColumnValue, true);
            const columnIndices = headerValueToColumns.get(sanitizedColumnValue) || new Set();
            columnIndices.add(columnIndex);
            headerValueToColumns.set(sanitizedColumnValue, columnIndices);
        }
    }
    const arrayLength = effectiveRowRange.endRowIndex - effectiveRowRange.startRowIndex + 1;
    // create and initialize data object. with each element an empty object
    // we allow nulls later to account from excluded rows.
    const data = Array.from({ length: arrayLength }, () => ({}));
    // initially all rows are assumed to be included.
    // for excluded rows we will replace the data[rowIndex] with null later.
    const errors = [];
    fields.forEach((entry) => {
        const { field: rawFieldName, importFormat } = entry;
        const pushNewError = (message) => errors.push({
            type: 'xlsx2Csv',
            fieldName: rawFieldName,
            message,
            sheetName,
        });
        const sanitizedFieldName = (0, helpers_1.getSanitizedFieldName)(rawFieldName, true);
        const columnIndices = headerValueToColumns.get(sanitizedFieldName);
        if (!columnIndices) {
            pushNewError(`[Field:${rawFieldName}] not found in header.`);
            return;
        }
        const columnIndicesArray = Array.from(columnIndices);
        if (columnIndicesArray.length !== 1) {
            // we have multiple columns with same value.
            const columnIndicesA1 = columnIndicesArray.map((columnIndex) => edit_1.default.utils.encode_col(columnIndex));
            pushNewError(`[Field:${rawFieldName}] appears in multiple columns:${columnIndicesA1.join(',')}.`);
            return;
        }
        const columnIndex = columnIndicesArray[0];
        // eslint-disable-next-line no-restricted-syntax
        for (let rowIndex = effectiveRowRange.startRowIndex; rowIndex <= effectiveRowRange.endRowIndex; 
        // eslint-disable-next-line no-plusplus
        rowIndex++) {
            const dataIndex = rowIndex - effectiveRowRange.startRowIndex;
            if (data[dataIndex] === null) {
                // this row is excluded by some other field.
                continue;
            }
            const cellAddressA1 = edit_1.default.utils.encode_cell({
                c: columnIndex,
                r: rowIndex,
            });
            const cellValue = getCellValue({
                sheet,
                cellAddressA1,
                importFormat,
            });
            if (entry.exclude) {
                // @xlsx2csv: todo: see if null check here is required
                //   it will be required for date fields perhpas.
                // if we are to exclude some values. see if the values
                // we got matches any of the excluded values.
                const effectiveCellValue = cellValue ? cellValue.value : null;
                if (entry.exclude.includes(effectiveCellValue)) {
                    data[dataIndex] = null;
                    continue;
                }
            }
            const { preSanitized, value } = cellValue;
            // sanitize the field
            try {
                const sanitizedValue = (0, helpers_1.getSanitizedDataTapeField)(entry, value, preSanitized);
                const dataObject = data[dataIndex];
                (0, common_1.finleyAssert)(dataObject !== null, 'data[dataIndex] must not be null');
                dataObject[sanitizedFieldName] = sanitizedValue;
            }
            catch (err) {
                const message = (0, typeguards_1.isInvalidField)(err)
                    ? `Cell:[${cellAddressA1}] for [field:${rawFieldName}] has [value:${err.value}] which is invalid for [ExpectedType:${err.expectedType}].`
                    : common_1.error.getErrorMessage(err);
                errors.push({
                    type: 'xlsx2Csv',
                    sheetName,
                    cell: cellAddressA1,
                    fieldName: rawFieldName,
                    message,
                });
            }
        }
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    // remoe any exculded rows.
    const csvArray = data.filter(common_1.typeguards.isNotNullOrUndefined);
    return {
        isValid: true,
        data: {
            outputBuffer: common_1.csvConversion.csvArrayToFinleyBuffer({
                csvArray,
                newline: '\n', // arrayToCsvBuffer uses \r\n by default
            }),
        },
        supplementals: [],
    };
};
const getOutputDataTape = (transformation) => {
    try {
        switch (transformation.mode) {
            case 'extractValues':
                return getOutputDataTapeValues(transformation);
            case 'convertRange':
                return getOutputDataTapeRange(transformation);
            default:
                return (0, common_1.finleyAssertUnreachable)(transformation);
        }
    }
    catch (error) {
        // caller requires that we throw an array of MetadataError.
        const errors = [];
        errors.push({
            type: 'metadataValidation',
            message: error instanceof Error
                ? error.message
                : 'Unexpected Error in xlsx2csv2:getOutputDataTape',
        });
        throw errors;
    }
};
const doTransform = ({ xlsBuffer, transformation, }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        switch (transformation.mode) {
            case 'extractValues':
                return doTransformValues({
                    xlsBuffer,
                    transformation,
                });
            case 'convertRange':
                return doTransformRange({
                    xlsBuffer,
                    transformation,
                });
            default:
                return (0, common_1.finleyAssertUnreachable)(transformation);
        }
    }
    catch (error) {
        return {
            isValid: false,
            errors: [
                {
                    type: 'metadataValidation',
                    message: error instanceof Error ? error.message : 'unknown xlsx2csv2 Error',
                },
            ],
        };
    }
});
exports.default = { getOutputDataTape, doTransform };
//# sourceMappingURL=xlsx2csv2.js.map